import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchServerProgressionOverview } from '../../api/progression';
import type { ProgressionBossRequirement, ProgressionExpansionSummary, ProgressionTierSummary } from '../../api/types';
import { serverAllStarsPath, serverBossProgressionPath, serverBossRankingMetricPath, serverBossRankingsPath, serverRankingsPath, serverTierProgressionPath } from '../../utils/siteContext';

export type RankingsSection = 'overview' | 'progression' | 'bosses' | 'allstars';

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

function bossId(boss: ProgressionBossRequirement) {
  return boss.raidBossId ?? boss.raidBoss?.id ?? boss.boss?.id ?? boss.id ?? null;
}

function bossSlug(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.slug ?? boss.boss?.slug ?? boss.slug ?? null;
}

function bossName(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.name ?? boss.boss?.name ?? boss.name ?? 'Unknown boss';
}

function slugify(value: string | number | null | undefined) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9`'._-]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function bossPathSlug(boss: ProgressionBossRequirement) {
  return bossSlug(boss) ?? slugify(bossName(boss)) ?? String(bossId(boss) ?? '');
}

function bossesForExpansion(expansion: ProgressionExpansionSummary | null) {
  if (!expansion) return [];

  const unique = new Map<string, ProgressionBossRequirement>();
  for (const tier of tiersFor(expansion)) {
    for (const objective of objectivesFor(tier)) {
      for (const boss of objective.requiredBosses ?? objective.bosses ?? []) {
        const key = String(bossId(boss) ?? bossSlug(boss) ?? bossName(boss));
        if (!unique.has(key)) unique.set(key, boss);
      }
    }
  }

  return [...unique.values()];
}

function selectedExpansion(expansions: ProgressionExpansionSummary[], expansionSlug: string) {
  return expansions.find((expansion) => expansion.slug === expansionSlug) ?? null;
}


function activeProgressionBossSlugFromPath(pathname: string, expansionSlug: string) {
  const prefix = `/rankings/progression/${encodeURIComponent(expansionSlug)}/`;
  if (!pathname.startsWith(prefix)) return null;

  const segment = pathname.slice(prefix.length).split('/')[0];
  return segment ? decodeURIComponent(segment) : null;
}

function activeBossSlugFromPath(pathname: string, expansionSlug: string) {
  const prefix = `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}/`;
  if (!pathname.startsWith(prefix)) return null;

  const segment = pathname.slice(prefix.length).split('/')[0];
  return segment ? decodeURIComponent(segment) : null;
}

function activeAllStarsBossSlugFromPath(pathname: string, expansionSlug: string) {
  const prefix = `/rankings/allstars/${encodeURIComponent(expansionSlug)}/`;
  if (!pathname.startsWith(prefix)) return null;

  const parts = pathname.slice(prefix.length).split('/');
  const bossSegment = parts[1];
  return bossSegment ? decodeURIComponent(bossSegment) : null;
}

export function RankingsSectionNav({
  activeSection,
  serverSlug,
  progressionExpansionSlug = 'classic',
  allStarsPartitionSlug = 'default',
  allStarsMetric = 'DAMAGE',
}: {
  activeSection: RankingsSection;
  serverSlug: string;
  progressionExpansionSlug?: string;
  allStarsPartitionSlug?: string;
  allStarsMetric?: 'DAMAGE' | 'HEALING';
}) {
  const location = useLocation();
  const progressionQuery = useQuery({
    queryKey: ['rankings-section-nav', 'progression', serverSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    staleTime: 60_000,
  });

  const expansions = progressionQuery.data?.progression?.expansions ?? [];
  const expansion = selectedExpansion(expansions, progressionExpansionSlug);
  const bosses = useMemo(() => bossesForExpansion(expansion), [expansion]);
  const activeProgressionBossSlug = activeProgressionBossSlugFromPath(location.pathname, progressionExpansionSlug);
  const activeBossSlug = activeBossSlugFromPath(location.pathname, progressionExpansionSlug);
  const activeAllStarsBossSlug = activeAllStarsBossSlugFromPath(location.pathname, progressionExpansionSlug);

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      to: serverRankingsPath(serverSlug),
    },
    {
      key: 'progression',
      label: 'Progression',
      to: serverTierProgressionPath(serverSlug, progressionExpansionSlug),
    },
    {
      key: 'bosses',
      label: 'Boss Rankings',
      to: serverBossRankingsPath(serverSlug, progressionExpansionSlug),
    },
    {
      key: 'allstars',
      label: 'All-Stars',
      to: serverAllStarsPath(serverSlug, {
        expansionSlug: progressionExpansionSlug,
        partitionSlug: allStarsPartitionSlug,
        bossSlug: 'all',
        metric: allStarsMetric,
      }),
    },
  ] as const;

  return (
    <div className="page-subnav rankings-section-subnav">
      <div className="content-wrapper">
        <ul className="nav secondary-tabs" aria-label="Rankings sections">
          {items.map((item) => {
            const isActive = activeSection === item.key;
            if (item.key === 'progression' || item.key === 'bosses' || item.key === 'allstars') {
              const activeDropdownBossSlug = item.key === 'progression' ? activeProgressionBossSlug : item.key === 'bosses' ? activeBossSlug : activeAllStarsBossSlug;
              const overviewIsActive = isActive && (!activeDropdownBossSlug || activeDropdownBossSlug === 'all');

              return (
                <li className="nav-item dropdown" key={item.key}>
                  <button
                    className={`nav-link dropdown-toggle ${isActive ? 'active' : ''}`}
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {item.label}
                  </button>
                  <ul className="dropdown-menu rankings-section-dropdown-menu">
                    <li>
                      <Link
                        className={`dropdown-item ${overviewIsActive ? 'active' : ''}`}
                        to={item.to}
                      >
                        Overview
                      </Link>
                    </li>
                    {bosses.map((boss) => {
                      const pathSlug = bossPathSlug(boss);
                      const to = item.key === 'progression'
                        ? serverBossProgressionPath(serverSlug, pathSlug, progressionExpansionSlug)
                        : item.key === 'bosses'
                          ? serverBossRankingMetricPath(serverSlug, progressionExpansionSlug, pathSlug, 'DAMAGE')
                          : serverAllStarsPath(serverSlug, {
                            expansionSlug: progressionExpansionSlug,
                            partitionSlug: allStarsPartitionSlug,
                            bossSlug: pathSlug,
                            metric: allStarsMetric,
                            all: true,
                            pageNum: 1,
                          });

                      return (
                        <li key={String(bossId(boss) ?? pathSlug)}>
                          <Link
                            className={`dropdown-item ${activeDropdownBossSlug === pathSlug ? 'active' : ''}`}
                            to={to}
                          >
                            {bossName(boss)}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            return (
              <li className="nav-item" key={item.key}>
                <Link
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  to={item.to}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
